/**
 * The organisation's knowledge base: any files people want eve to know, with
 * nothing to fill in. A file dropped here is stored, parsed, embedded into
 * the organisation's own search index and found by eve in every conversation
 * — no owners, review cycles or jurisdictions, because the firm is not
 * accountable for it the way it is for a policy.
 *
 * Under the hood a knowledge file is a library document carrying the
 * `reference` flag (see `isKnowledgeDocument`): the same upload, ingestion
 * and org-scoped retrieval as a policy, and the documents eve itself files as
 * "knowledge" appear here too.
 *
 * @module components/regulation/knowledge/KnowledgeSection
 */
import type { HairlineProps } from "@lucasmarkes/hairline/react";
import { KNOWLEDGE_ACCEPT, KNOWLEDGE_EXTENSIONS_LABEL, knowledgeFileProblem } from "@repo/shared/knowledgeFiles";
import {
  AlertCircleIcon,
  BookOpenIcon,
  CheckCircle2Icon,
  FileIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  HistoryIcon,
  LoaderCircleIcon,
  MoreHorizontalIcon,
  SearchIcon,
  UploadIcon,
  XIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from "react";

import { cn } from "~/lib/utils";

import { UNAVAILABLE_MESSAGES, useOrgProfile } from "../../../state/orgProfile";
import { usePolicies, type Policy } from "../../../state/policies";
import { formatRelativeTimeLabel } from "../../../timestampFormat";
import { Shelf } from "../../hairline/figures";
import { Button } from "../../ui/button";
import { HairlineArt } from "../../ui/hairline-art";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../../ui/input-group";
import { Menu, MenuItem, MenuPopup, MenuSeparator, MenuTrigger } from "../../ui/menu";
import { Sheet, SheetHeader, SheetPanel, SheetPopup, SheetTitle } from "../../ui/sheet";
import { Skeleton } from "../../ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { RegulationSectionPage } from "../RegulationSectionPage";
import { useRegulationOrg } from "../useRegulationOrg";
import {
  KNOWLEDGE_MAX_MB,
  knowledgeDocuments,
  knowledgeNeedsPolling,
  knowledgeSizeLabel,
  knowledgeStatus,
  knowledgeTypeLabel,
  knowledgeUpdatedAt,
  knowledgeUpdatedBy,
  knowledgeVersionHistory,
  matchesKnowledgeQuery,
  opensInline,
  planKnowledgeUploads,
  uploadKey,
  uploaderLabel,
  type KnowledgeDocument,
  type KnowledgeStatus,
  type RejectedFile,
} from "./knowledgeBase.logic";

/** Re-read the list this often while a file is still being processed. */
const POLL_MS = 4_000;

interface UploadRow {
  readonly organizationId: string;
  readonly key: string;
  readonly name: string;
  readonly size: number;
  readonly error: string | null;
}

const STATUS_PILL: Record<KnowledgeStatus, { label: string; className: string; icon: ReactNode; title?: string }> = {
  ready: {
    label: "Ready",
    className: "bg-emerald-600/10 font-medium text-emerald-600 dark:text-emerald-300/90",
    icon: <CheckCircle2Icon className="size-3" />,
    title: "Indexed — eve can search it",
  },
  processing: {
    label: "Processing",
    className: "bg-muted text-muted-foreground",
    icon: <LoaderCircleIcon className="size-3 animate-spin" />,
    title: "Being read and indexed",
  },
  stalled: {
    label: "Stalled",
    className: "bg-destructive/10 font-medium text-destructive",
    icon: <AlertCircleIcon className="size-3" />,
    title: "Processing never finished. Remove the file and upload it again.",
  },
  failed: {
    label: "Failed",
    className: "bg-destructive/10 text-destructive",
    icon: <AlertCircleIcon className="size-3" />,
    title: "The file could not be read. Remove it and upload it again.",
  },
};

function StatusPill({ status }: { status: KnowledgeStatus }) {
  const pill = STATUS_PILL[status];
  return (
    <span
      title={pill.title}
      className={cn("flex w-fit shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs", pill.className)}
    >
      {pill.icon}
      {pill.label}
    </span>
  );
}

function FileGlyph({ filename }: { filename: string }) {
  const label = knowledgeTypeLabel(filename);
  const Icon = label === "CSV" ? FileSpreadsheetIcon : label === "File" ? FileIcon : FileTextIcon;
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
      <Icon className="size-4" />
    </span>
  );
}

function VersionChip({ version }: { version: number }) {
  return (
    <span className="shrink-0 rounded-md border border-border/70 px-1.5 py-px text-[11px] leading-4 font-medium text-muted-foreground tabular-nums">
      v{version}
    </span>
  );
}

function KnowledgeRow({
  document,
  now,
  canManage,
  uploadingVersion,
  onOpen,
  onDownload,
  onShowVersions,
  onUploadVersion,
  onRemove,
}: {
  document: KnowledgeDocument;
  now: number;
  canManage: boolean;
  /** A new version of this file is on its way up. */
  uploadingVersion: boolean;
  onOpen: () => void;
  onDownload: () => void;
  onShowVersions: () => void;
  onUploadVersion: () => void;
  onRemove: () => void;
}) {
  const updatedBy = knowledgeUpdatedBy(document);
  const uploader = uploaderLabel(updatedBy);
  const updatedAt = knowledgeUpdatedAt(document);
  const updated = formatRelativeTimeLabel(updatedAt);
  const version = document.version ?? 1;
  // The API refuses a new version while the current one is still being read.
  const status = knowledgeStatus(document, now);
  return (
    <TableRow className="h-14 cursor-pointer" onClick={onOpen}>
      <TableCell className="px-4">
        <div className="flex min-w-0 items-center gap-3">
          <FileGlyph filename={document.filename} />
          <div className="min-w-0">
            {/* A button keeps the row keyboard-reachable; the row click is the pointer affordance. */}
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpen();
                }}
                className="block max-w-[220px] cursor-pointer truncate text-left text-sm font-medium text-foreground hover:underline sm:max-w-[360px] lg:max-w-[440px]"
                title={document.filename}
              >
                {document.filename}
              </button>
              {version > 1 ? (
                <button
                  type="button"
                  className="cursor-pointer"
                  title={`Version ${version} — see every version`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onShowVersions();
                  }}
                >
                  <VersionChip version={version} />
                </button>
              ) : null}
            </div>
            {/* On narrow screens the hidden columns collapse into one line. */}
            <p className="truncate text-xs text-muted-foreground md:hidden">
              {[knowledgeTypeLabel(document.filename), knowledgeSizeLabel(document), updated].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden px-4 text-xs text-muted-foreground md:table-cell">
        {knowledgeTypeLabel(document.filename)}
      </TableCell>
      <TableCell className="hidden px-4 text-xs text-muted-foreground tabular-nums md:table-cell">
        {knowledgeSizeLabel(document)}
      </TableCell>
      <TableCell className="hidden px-4 lg:table-cell">
        <span className="block text-xs text-foreground" title={updatedBy ?? undefined}>
          {uploader ?? "eve"}
        </span>
        <span className="block text-xs text-muted-foreground" title={new Date(updatedAt).toLocaleString()}>
          {updated}
        </span>
      </TableCell>
      <TableCell className="px-4">
        {uploadingVersion ? <UploadingPill /> : <StatusPill status={status} />}
      </TableCell>
      <TableCell className="px-4">
        {/* Row actions stop propagation: opening the menu must not also open the file. */}
        <div onClick={(event) => event.stopPropagation()}>
          <Menu>
            <MenuTrigger
              render={
                <Button type="button" size="icon-xs" variant="ghost" aria-label={`Actions for ${document.filename}`} />
              }
            >
              <MoreHorizontalIcon className="size-4" />
            </MenuTrigger>
            <MenuPopup align="end">
              {opensInline(document.filename) ? <MenuItem onClick={onOpen}>Open</MenuItem> : null}
              <MenuItem onClick={onDownload}>Download</MenuItem>
              <MenuItem onClick={onShowVersions}>
                {version > 1 ? `Versions (${version})` : "Versions"}
              </MenuItem>
              {canManage ? (
                <>
                  <MenuItem disabled={uploadingVersion || status === "processing" || status === "stalled"} onClick={onUploadVersion}>
                    Upload new version
                  </MenuItem>
                  <MenuSeparator />
                  <MenuItem variant="destructive" onClick={onRemove}>
                    Remove
                  </MenuItem>
                </>
              ) : null}
            </MenuPopup>
          </Menu>
        </div>
      </TableCell>
    </TableRow>
  );
}

function UploadingPill() {
  return (
    <span className="flex w-fit shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
      <LoaderCircleIcon className="size-3 animate-spin" />
      Uploading
    </span>
  );
}

function UploadingRow({ row, onDismiss }: { row: UploadRow; onDismiss: () => void }) {
  return (
    <TableRow className="h-14 hover:bg-transparent">
      <TableCell className="px-4">
        <div className="flex min-w-0 items-center gap-3">
          <FileGlyph filename={row.name} />
          <div className="min-w-0">
            <p className="max-w-[220px] truncate text-sm font-medium text-foreground sm:max-w-[360px] lg:max-w-[480px]">
              {row.name}
            </p>
            {row.error ? <p className="truncate text-xs text-destructive">{row.error}</p> : null}
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden px-4 text-xs text-muted-foreground md:table-cell">
        {knowledgeTypeLabel(row.name)}
      </TableCell>
      <TableCell className="hidden px-4 text-xs text-muted-foreground tabular-nums md:table-cell">
        {knowledgeSizeLabel({ sizeBytes: row.size })}
      </TableCell>
      <TableCell className="hidden px-4 lg:table-cell" />
      <TableCell className="px-4">
        {row.error ? (
          <StatusPill status="failed" />
        ) : (
          <UploadingPill />
        )}
      </TableCell>
      <TableCell className="px-4">
        {row.error ? (
          <Button type="button" size="icon-xs" variant="ghost" aria-label={`Dismiss ${row.name}`} onClick={onDismiss}>
            <XIcon className="size-4" />
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

const ACCEPTS_COPY = `${KNOWLEDGE_EXTENSIONS_LABEL} · up to ${KNOWLEDGE_MAX_MB} MB each`;

export function KnowledgeSection() {
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const { snapshot, unavailableReason, isPending, uploadDocument, uploadVersion, download, previewUrl, remove, refresh } =
    usePolicies(organizationId);
  const { canManage } = useOrgProfile(organizationId);

  const inputRef = useRef<HTMLInputElement>(null);
  const versionInputRef = useRef<HTMLInputElement>(null);
  // The file "Upload new version" was picked for, while its file picker is open.
  const versionTargetRef = useRef<string | null>(null);
  // Documents with a new version on its way up.
  const [versionUploads, setVersionUploads] = useState<ReadonlySet<string>>(() => new Set());
  const [versionsOf, setVersionsOf] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [uploads, setUploads] = useState<ReadonlyArray<UploadRow>>([]);
  const [rejected, setRejected] = useState<ReadonlyArray<RejectedFile>>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  // Drag depth, not a boolean: dragging over a child fires dragleave on the
  // parent, and a boolean would flicker the overlay off mid-drag.
  const [dragDepth, setDragDepth] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  // Documents this page just created, kept until the refreshed list carries
  // them: in between, re-dropping the same name must still find it and file a
  // new version, not a second copy.
  // Each is tagged with its organisation: one created in another organisation
  // must never show here, nor become the target of a version.
  const [created, setCreated] = useState<ReadonlyArray<{ readonly organizationId: string; readonly policy: Policy }>>([]);
  const documents = useMemo(() => {
    const listed = snapshot?.policies ?? [];
    const listedIds = new Set(listed.map((policy) => policy.id));
    const pending = created
      .filter((entry) => entry.organizationId === organizationId && !listedIds.has(entry.policy.id))
      .map((entry) => entry.policy);
    return knowledgeDocuments([...listed, ...pending]);
  }, [snapshot, created, organizationId]);
  useEffect(() => {
    const listed = new Set((snapshot?.policies ?? []).map((policy) => policy.id));
    setCreated((current) =>
      current.some((entry) => listed.has(entry.policy.id)) ? current.filter((entry) => !listed.has(entry.policy.id)) : current,
    );
  }, [snapshot]);
  // Uploads still on their way to another organisation are not this page's.
  const visibleUploads = uploads.filter((row) => row.organizationId === organizationId);
  const filtered = useMemo(
    () => documents.filter((document) => matchesKnowledgeQuery(document, query)),
    [documents, query],
  );

  // Processing finishes on the API, not here; re-read until nothing is in flight.
  const polling = knowledgeNeedsPolling(documents, now);
  useEffect(() => {
    if (!polling) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
      refresh();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [polling, refresh]);

  const sendVersion = useCallback(
    async (documentId: string, file: File) => {
      setVersionUploads((current) => new Set(current).add(documentId));
      const outcome = await uploadVersion(documentId, file, null);
      setVersionUploads((current) => {
        const next = new Set(current);
        next.delete(documentId);
        return next;
      });
      if (outcome.error !== null) setActionError(`${file.name}: ${outcome.error}`);
      setNow(Date.now());
    },
    [uploadVersion],
  );

  const startUploads = useCallback(
    async (files: ReadonlyArray<File>) => {
      if (!organizationId) return;
      setActionError(null);
      const plan = planKnowledgeUploads(files, {
        inFlight: uploads.filter((row) => row.error === null && row.organizationId === organizationId),
        existing: documents,
        versionsInFlight: versionUploads,
        now: Date.now(),
      });
      setRejected(plan.rejected);
      // A file named like one already here replaces it, keeping the old one as
      // history. Marked as uploading at once, sent one at a time below.
      if (plan.versions.length > 0) {
        setVersionUploads((current) => new Set([...current, ...plan.versions.map((entry) => entry.documentId)]));
      }
      if (plan.accepted.length === 0) {
        for (const { file, documentId } of plan.versions) await sendVersion(documentId, file);
        return;
      }
      setUploads((current) => [
        ...plan.accepted.map((file) => ({ organizationId, key: uploadKey(file), name: file.name, size: file.size, error: null })),
        ...current.filter(
          (row) => row.organizationId !== organizationId || !plan.accepted.some((file) => uploadKey(file) === row.key),
        ),
      ]);
      // One at a time, versions included: each file travels as base64 JSON,
      // and several 25 MB bodies at once is how a browser tab runs out of memory.
      for (const { file, documentId } of plan.versions) await sendVersion(documentId, file);
      for (const file of plan.accepted) {
        const key = uploadKey(file);
        // This organisation's row only: the same file may be uploading elsewhere.
        const isRow = (row: UploadRow) => row.organizationId === organizationId && row.key === key;
        const outcome = await uploadDocument({ file, jurisdictionSlugs: [], reference: true });
        if (outcome.error === null) setCreated((current) => [...current, { organizationId, policy: outcome.value }]);
        setUploads((current) =>
          outcome.error === null
            ? current.filter((row) => !isRow(row))
            : current.map((row) => (isRow(row) ? { ...row, error: outcome.error } : row)),
        );
      }
      setNow(Date.now());
    },
    [organizationId, uploadDocument, uploads, documents, versionUploads, sendVersion],
  );

  const pickVersion = (documentId: string) => {
    versionTargetRef.current = documentId;
    versionInputRef.current?.click();
  };

  const openFile = (document: KnowledgeDocument) => {
    const url = opensInline(document.filename) ? previewUrl(document.id) : null;
    if (url) window.open(url, "_blank", "noopener");
    else download(document.id);
  };

  const uploadButton = canManage && org ? (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="cursor-pointer gap-1.5"
      disabled={snapshot === null}
      onClick={() => inputRef.current?.click()}
    >
      <UploadIcon className="size-3.5" />
      Upload files
    </Button>
  ) : null;

  const body = (() => {
    // The table's own shape while it loads, not a sentence in an empty box.
    if ((org === null && isLoading) || (org !== null && isPending && snapshot === null && unavailableReason === null)) {
      return <KnowledgeSkeleton />;
    }
    if (org === null) {
      return <EmptyPanel title="No organization selected" body="Choose an organization to see its knowledge." />;
    }
    if (unavailableReason !== null) {
      return <EmptyPanel title="Knowledge is unavailable" body={UNAVAILABLE_MESSAGES[unavailableReason]} />;
    }
    if (documents.length === 0 && visibleUploads.length === 0) {
      return (
        <EmptyPanel
          art={Shelf}
          title="No knowledge yet"
          body={
            canManage
              ? "Drop in anything eve should know about your organisation — handbooks, guidance, memos, FAQs, notes. It searches these files in every conversation."
              : "Files an admin adds here become knowledge eve searches in every conversation."
          }
          footer={
            canManage ? (
              <div className="mt-5 flex flex-col items-center gap-2">
                {uploadButton}
                <p className="text-xs text-muted-foreground">{ACCEPTS_COPY}</p>
              </div>
            ) : null
          }
        />
      );
    }
    return (
      <>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-56 flex-1">
            <InputGroup className="h-8">
              <InputGroupAddon className="text-muted-foreground">
                <SearchIcon className="size-3.5" />
              </InputGroupAddon>
              <InputGroupInput
                value={query}
                onChange={(event) => setQuery(event.currentTarget.value)}
                placeholder="Search files"
                aria-label="Search files"
                className="text-sm"
              />
            </InputGroup>
          </div>
          <span className="ms-auto shrink-0 pe-1 text-xs text-muted-foreground tabular-nums">
            {filtered.length === documents.length
              ? `${documents.length} ${documents.length === 1 ? "file" : "files"}`
              : `${filtered.length} of ${documents.length} files`}
          </span>
        </div>
        {/* overflow-x-auto, never hidden: a clipped Status column is a lie. */}
        <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
          <Table>
            <TableHeader>
              <TableRow className="border-b bg-muted/50 hover:bg-muted/50">
                <TableHead className="h-10 px-4 font-medium text-foreground/80">Name</TableHead>
                <TableHead className="hidden h-10 w-[110px] px-4 font-medium text-foreground/80 md:table-cell">
                  Type
                </TableHead>
                <TableHead className="hidden h-10 w-[90px] px-4 font-medium text-foreground/80 md:table-cell">
                  Size
                </TableHead>
                <TableHead className="hidden h-10 w-[150px] px-4 font-medium text-foreground/80 lg:table-cell">
                  Updated
                </TableHead>
                <TableHead className="h-10 w-[130px] px-4 font-medium text-foreground/80">Status</TableHead>
                <TableHead className="h-10 w-[56px] px-4">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleUploads.map((row) => (
                <UploadingRow
                  key={row.key}
                  row={row}
                  onDismiss={() =>
                    setUploads((current) =>
                      current.filter((entry) => entry.organizationId !== row.organizationId || entry.key !== row.key),
                    )
                  }
                />
              ))}
              {filtered.length === 0 && visibleUploads.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={6} className="h-24 text-center text-sm text-muted-foreground">
                    No files match “{query.trim()}”.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((document) => (
                  <KnowledgeRow
                    key={document.id}
                    document={document}
                    now={now}
                    canManage={canManage}
                    uploadingVersion={versionUploads.has(document.id)}
                    onOpen={() => openFile(document)}
                    onDownload={() => download(document.id)}
                    onShowVersions={() => setVersionsOf(document.id)}
                    onUploadVersion={() => pickVersion(document.id)}
                    onRemove={() => {
                      setActionError(null);
                      void remove(document.id).then((error) => {
                        setActionError(error);
                        if (error === null) setCreated((current) => current.filter((entry) => entry.policy.id !== document.id));
                      });
                    }}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {canManage ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Drop files anywhere here · same name = new version · {ACCEPTS_COPY}
          </p>
        ) : null}
      </>
    );
  })();

  // Not before the list has loaded: until then a file named like one already
  // here cannot be recognised as its new version.
  const dropEnabled = canManage && org !== null && unavailableReason === null && snapshot !== null;

  return (
    <RegulationSectionPage
      title="Knowledge"
      description="Organisation-wide files eve searches whenever they are relevant, in every conversation."
      emptyIcon={BookOpenIcon}
      emptyTitle=""
      emptyBody=""
      actions={documents.length > 0 || visibleUploads.length > 0 ? uploadButton : null}
    >
      <div
        className="relative mt-6"
        onDragEnter={(event) => {
          if (!dropEnabled) return;
          event.preventDefault();
          setDragDepth((depth) => depth + 1);
        }}
        onDragOver={(event) => {
          if (!dropEnabled) return;
          event.preventDefault();
        }}
        onDragLeave={() => {
          if (!dropEnabled) return;
          setDragDepth((depth) => Math.max(0, depth - 1));
        }}
        onDrop={(event) => {
          if (!dropEnabled) return;
          event.preventDefault();
          setDragDepth(0);
          void startUploads(Array.from(event.dataTransfer?.files ?? []));
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={KNOWLEDGE_ACCEPT}
          className="hidden"
          onChange={(event) => {
            const files = Array.from(event.currentTarget.files ?? []);
            // Cleared so picking the same file again after a failure still fires.
            event.currentTarget.value = "";
            void startUploads(files);
          }}
        />
        {dragDepth > 0 ? (
          <div className="pointer-events-none absolute inset-0 z-20 flex min-h-40 items-center justify-center rounded-xl border-2 border-dashed border-primary/50 bg-background/80 backdrop-blur-[1px]">
            <p className="flex items-center gap-2 text-sm font-medium text-foreground">
              <UploadIcon className="size-4" />
              Drop files to add them to your knowledge
            </p>
          </div>
        ) : null}
        {rejected.length > 0 ? (
          <div
            role="alert"
            className="mb-3 flex items-start justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
          >
            <ul className="min-w-0 space-y-0.5">
              {rejected.map((file, index) => (
                <li key={`${file.name}:${index}`} className="truncate">
                  <span className="font-medium">{file.name}</span> — {file.reason}
                </li>
              ))}
            </ul>
            <button
              type="button"
              aria-label="Dismiss"
              className="shrink-0 cursor-pointer opacity-70 hover:opacity-100"
              onClick={() => setRejected([])}
            >
              <XIcon className="size-3.5" />
            </button>
          </div>
        ) : null}
        <input
          ref={versionInputRef}
          type="file"
          accept={KNOWLEDGE_ACCEPT}
          className="hidden"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = "";
            const documentId = versionTargetRef.current;
            versionTargetRef.current = null;
            if (!file || !documentId) return;
            const problem = knowledgeFileProblem(file);
            if (problem !== null) {
              setActionError(`${file.name}: ${problem}`);
              return;
            }
            setActionError(null);
            void sendVersion(documentId, file);
          }}
        />
        {actionError ? (
          <p role="alert" className="mb-2 text-xs text-destructive">
            {actionError}
          </p>
        ) : null}
        {body}
        <KnowledgeVersionsSheet
          document={documents.find((document) => document.id === versionsOf) ?? null}
          canManage={canManage}
          uploadingVersion={versionsOf !== null && versionUploads.has(versionsOf)}
          onClose={() => setVersionsOf(null)}
          onOpen={(document) => openFile(document)}
          onDownload={(documentId, version) => download(documentId, version)}
          onUploadVersion={(documentId) => pickVersion(documentId)}
        />
      </div>
    </RegulationSectionPage>
  );
}

function EmptyPanel({
  art,
  title,
  body,
  footer,
}: {
  art?: ComponentType<HairlineProps>;
  title: string;
  body: string;
  footer?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border/60 bg-card px-6 py-16 text-center">
      {art ? (
        <HairlineArt figure={art} surface="card" className="mb-3 w-48 sm:w-56" />
      ) : (
        <span className="mb-3 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <BookOpenIcon className="size-5" />
        </span>
      )}
      <p className="text-base font-medium text-foreground">{title}</p>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">{body}</p>
      {footer}
    </div>
  );
}

function KnowledgeSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading knowledge">
      <div className="mb-3 flex items-center gap-2">
        <Skeleton className="h-8 flex-1 rounded-lg" />
        <Skeleton className="h-4 w-12" />
      </div>
      <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
        <div className="h-10 border-b bg-muted/50" />
        {[0.62, 0.48, 0.55, 0.4].map((width, index) => (
          <div key={index} className="flex h-14 items-center gap-3 border-b border-border/40 px-4 last:border-b-0">
            <Skeleton className="size-8 rounded-md" />
            <Skeleton className="h-3.5" style={{ width: `${width * 40}%` }} />
            <Skeleton className="ms-auto hidden h-3 w-12 md:block" />
            <Skeleton className="hidden h-3 w-10 md:block" />
            <Skeleton className="hidden h-3 w-20 lg:block" />
            <Skeleton className="h-5 w-16 rounded-full" />
            <span className="w-6" />
          </div>
        ))}
      </div>
    </div>
  );
}

function KnowledgeVersionsSheet({
  document,
  canManage,
  uploadingVersion,
  onClose,
  onOpen,
  onDownload,
  onUploadVersion,
}: {
  document: KnowledgeDocument | null;
  canManage: boolean;
  uploadingVersion: boolean;
  onClose: () => void;
  onOpen: (document: KnowledgeDocument) => void;
  onDownload: (documentId: string, version?: number) => void;
  onUploadVersion: (documentId: string) => void;
}) {
  const history = document ? knowledgeVersionHistory(document) : [];
  return (
    <Sheet open={document !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <SheetPopup className="w-full max-w-md">
        {document ? (
          <>
            <SheetHeader className="gap-3 pb-4">
              <div className="flex items-center gap-3 pe-8">
                <FileGlyph filename={document.filename} />
                <div className="min-w-0">
                  <SheetTitle className="truncate text-base leading-snug" title={document.filename}>
                    {document.filename}
                  </SheetTitle>
                  <p className="text-xs text-muted-foreground">
                    {[knowledgeTypeLabel(document.filename), knowledgeSizeLabel(document)].join(" · ")}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" className="cursor-pointer" onClick={() => onOpen(document)}>
                  {opensInline(document.filename) ? "Open" : "Download"}
                </Button>
                {canManage ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="cursor-pointer gap-1.5"
                    disabled={uploadingVersion || document.status === "processing"}
                    onClick={() => onUploadVersion(document.id)}
                  >
                    {uploadingVersion ? (
                      <LoaderCircleIcon className="size-3.5 animate-spin" />
                    ) : (
                      <UploadIcon className="size-3.5" />
                    )}
                    {uploadingVersion ? "Uploading…" : "Upload new version"}
                  </Button>
                ) : null}
              </div>
            </SheetHeader>
            <SheetPanel>
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <HistoryIcon className="size-3.5" />
                {history.length === 1 ? "1 version" : `${history.length} versions`}
              </h3>
              <ol className="divide-y divide-border/60 rounded-xl border border-border/60">
                {history.map((entry) => (
                  <li key={entry.version} className="flex items-start gap-3 px-3 py-2.5">
                    <VersionChip version={entry.version} />
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 text-sm text-foreground">
                        <span className="truncate" title={entry.filename}>
                          {entry.filename}
                        </span>
                        {entry.current ? (
                          <span className="shrink-0 rounded-full bg-emerald-600/10 px-1.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-300/90">
                            Current
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-muted-foreground" title={new Date(entry.uploadedAt).toLocaleString()}>
                        {[uploaderLabel(entry.uploadedByEmail) ?? "eve", formatRelativeTimeLabel(entry.uploadedAt)].join(" · ")}
                      </p>
                      {entry.note ? <p className="mt-1 text-xs text-foreground/80">{entry.note}</p> : null}
                    </div>
                    <Button
                      type="button"
                      size="xs"
                      variant="ghost"
                      className="shrink-0 cursor-pointer"
                      onClick={() => onDownload(document.id, entry.current ? undefined : entry.version)}
                    >
                      Download
                    </Button>
                  </li>
                ))}
              </ol>
              <p className="mt-3 text-xs text-muted-foreground">
                eve searches the current version only. Earlier versions stay downloadable.
              </p>
            </SheetPanel>
          </>
        ) : null}
      </SheetPopup>
    </Sheet>
  );
}
